import { useCallback } from 'react';

import {
  Job,
  JobEntity,
  JobOperation,
  JobStatus,
  getAllJobs,
  ListJobParams,
} from '@commenda/api';

import { IndirectTaxJobHelper } from '../helpers/IndirectTaxJobHelper';

export interface JobWatcherState {
  jobs: Job[];
  refreshJobs: () => void;
  isJobInProgress: (entity: JobEntity) => boolean;
  isJobInProgressForOperation: (
    entity: JobEntity,
    operation: JobOperation
  ) => boolean;
  getJobsByEntity: (entity: JobEntity) => Job[];
  getJobsByEntityAndOperation: (
    entity: JobEntity,
    operation: JobOperation
  ) => Job[];
}

export const useIndirectTaxJobWatcher = (
  params: ListJobParams
): JobWatcherState => {
  const { jobs, mutate } = getAllJobs({
    params: {
      limit: IndirectTaxJobHelper.LIST_DEFAULT_SIZE,
      ...params,
    },
    options: IndirectTaxJobHelper.FETCHER_DEFAULT_OPTIONS,
  });

  const isJobInProgress = useCallback(
    (entity: JobEntity): boolean => {
      if (!jobs) return false;
      return jobs.some(
        (job) =>
          job.entity === entity &&
          (job.status === JobStatus.PENDING ||
            job.status === JobStatus.PROCESSING ||
            job.status === JobStatus.QUEUED)
      );
    },
    [jobs]
  );

  const isJobInProgressForOperation = useCallback(
    (entity: JobEntity, operation: JobOperation): boolean => {
      if (!jobs) return false;
      return jobs.some(
        (job) =>
          job.entity === entity &&
          job.operation === operation &&
          (job.status === JobStatus.PENDING ||
            job.status === JobStatus.PROCESSING ||
            job.status === JobStatus.QUEUED)
      );
    },
    [jobs]
  );

  const getJobsByEntity = useCallback(
    (entity: JobEntity): Job[] => {
      if (!jobs) return [];
      return jobs.filter((job) => job.entity === entity);
    },
    [jobs]
  );

  const getJobsByEntityAndOperation = useCallback(
    (entity: JobEntity, operation: JobOperation): Job[] => {
      if (!jobs) return [];
      return jobs.filter(
        (job) => job.entity === entity && job.operation === operation
      );
    },
    [jobs]
  );

  return {
    jobs,
    refreshJobs: mutate,
    isJobInProgress,
    isJobInProgressForOperation,
    getJobsByEntity,
    getJobsByEntityAndOperation,
  };
};
